/* import __COLOCATED_TEMPLATE__ from './special-notice.hbs'; */
/* RESPONSIBLE TEAM: team-messenger */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type MessengerSettingsService from 'embercom/services/messenger-settings-service';
import type TemporaryExpectationsModel from 'embercom/models/messenger-settings/temporary-expectations';
import type IntlService from 'embercom/services/intl';
import type AppService from 'embercom/services/app-service';
import type Store from '@ember-data/store';

type SimpleLocale = { localeId: string; isPermitted: boolean; name: string };

export interface Args {
  temporaryExpectations: TemporaryExpectationsModel;
  languages: {
    supportedLocales: Array<SimpleLocale>;
    availableLocales: Array<SimpleLocale>;
    defaultLocale: string;
  };
}

export default class SpecialNotice extends Component<Args> {
  @service declare messengerSettingsService: MessengerSettingsService;
  @service declare appService: AppService;
  @service declare attributeService: any;
  @service declare intl: IntlService;
  @service declare store: Store;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.initializeAudienceState();
  }

  private ensurePredicateGroupsExist() {
    if (!this.temporaryExpectations.predicateGroup) {
      this.temporaryExpectations.predicateGroup = this.store.createFragment(
        'predicates/predicate-group',
        {
          predicates: [],
        },
      );
    }
    if (!this.temporaryExpectations.rolePredicateGroup) {
      this.temporaryExpectations.rolePredicateGroup = this.store.createFragment(
        'predicates/predicate-group',
        {
          predicates: [],
        },
      );
    }
    if (!this.temporaryExpectations.clientPredicateGroup) {
      this.temporaryExpectations.clientPredicateGroup = this.store.createFragment(
        'predicates/predicate-group',
        {
          predicates: [],
        },
      );
    }
    if (!this.temporaryExpectations.defaultPredicateGroup) {
      this.temporaryExpectations.defaultPredicateGroup = this.store.createFragment(
        'predicates/predicate-group',
        {
          predicates: [],
        },
      );
    }
  }

  get languages() {
    return this.args.languages;
  }

  get permittedLocales() {
    let defaultLocale = this.languages.supportedLocales.findBy(
      'localeId',
      this.languages.defaultLocale,
    );

    let otherPermittedLocales = this.languages.supportedLocales.filter(
      (locale) => locale.localeId !== this.languages.defaultLocale && locale.isPermitted,
    );
    return [defaultLocale, ...otherPermittedLocales].compact();
  }

  get shouldShowAudienceSelector() {
    return (
      this.appService.app?.canUseFeature('team-messenger-special-notice-audience-targeting') ??
      false
    );
  }

  get app() {
    return this.appService.app;
  }

  get temporaryExpectations() {
    return this.args.temporaryExpectations;
  }

  private initializeAudienceState() {
    this.ensurePredicateGroupsExist();

    if (!this.hasAnyPredicates()) {
      let everyoneRolePredicate = this.store.createFragment('predicates/role', {
        attribute: 'role',
        type: 'role',
        comparison: 'in',
        value: ['contact_role', 'user_role', 'visitor_role'],
        subclass: 'predicates/role',
      });
      this.temporaryExpectations.rolePredicateGroup.predicates = [everyoneRolePredicate];
    }
  }

  get rulesEditorConfig() {
    let editorConfiguration = {
      workflowMatchingTargetingAttributeIdentifiers: this.getAttributeIdentifiers(),
      workflowMatchingTargetingAttributes: this.getAttributeGroups(),
      supportsUserTargeting: true,
      supportsConversationChannelTargeting: false,
      targetingAttributePickerLabel: this.intl.t(
        'operator.workflows.visual-builder.workflow-audience.add-audience',
      ),
      targetingSectionTitle: '',
    };

    return {
      editorConfiguration,
      showAudiencePreview: false,
      hideAddRule: false,
    };
  }

  private hasAnyPredicates(): boolean {
    let hasPredicates = this.temporaryExpectations.predicateGroup?.predicates?.length > 0;
    let hasRolePredicates = this.temporaryExpectations.rolePredicateGroup?.predicates?.length > 0;
    let hasClientPredicates =
      this.temporaryExpectations.clientPredicateGroup?.predicates?.length > 0;
    let hasDefaultPredicates =
      this.temporaryExpectations.defaultPredicateGroup?.predicates?.length > 0;

    return hasPredicates || hasRolePredicates || hasClientPredicates || hasDefaultPredicates;
  }

  private getAttributeIdentifiers() {
    let {
      filterableUserAttributesForMessaging: userAttrs,
      filterableCompanyAttributesForMessaging: companyAttrs,
    } = this.attributeService;

    let safeUserAttrs = userAttrs || [];
    let safeCompanyAttrs = companyAttrs || [];
    let validAttributes = this.app.companiesActive
      ? [...safeUserAttrs, ...safeCompanyAttrs]
      : safeUserAttrs;
    let identifiers = validAttributes
      .filter((attr: { identifier: any }) => attr && attr.identifier)
      .map((attr: { identifier: any }) => attr.identifier);
    return [...identifiers, 'role'];
  }

  private getAttributeGroups() {
    let {
      filterableUserAttributesForMessaging: userAttrs,
      filterableCompanyAttributesForMessaging: companyAttrs,
    } = this.attributeService;

    let groups = [
      {
        heading: this.intl.t('operator.workflows.visual-builder.workflow-audience.person-data'),
        attributes: userAttrs || [],
      },
    ];
    if (this.app.companiesActive) {
      groups.push({
        heading: this.intl.t('operator.workflows.visual-builder.workflow-audience.company-data'),
        attributes: companyAttrs || [],
      });
    }
    return groups;
  }

  @action
  navigateToAiAgentExpectations() {
    this.messengerSettingsService.navigate('fin', 'set-expectation');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'MessengerSettings::Content::SpecialNotice': typeof SpecialNotice;
    'messenger-settings/content/special-notice': typeof SpecialNotice;
  }
}
